import { lazy, useContext } from 'react';
import { Route, Navigate, Routes } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { usePageView } from '@koob/google-tag-manager';
import { AuthContext } from 'contexts';
import Layout from 'components/Layout';
import AppProvider from 'providers/AppProvider';
import RoutesProvider from 'providers/RoutesProvider';
import { KoobMapsProvider } from '@koob/maps';
import { GOOGLE_MAPS_API_KEY, GOOGLE_MAPS_MAP_ID } from './constants';
import ToConnectionRequest from './containers/ToConnectionRequest';
import ExperienceSettingsRoutes from './containers/ExperiencesSettings/routes';
import ExperiencesRoutes from 'containers/Experiences/routes';
import BookingProvider from './providers/BookingProvider';
import TripDesignerConfigProvider from 'providers/TripDesignerConfigProvider';
import { NotificationsProvider } from '@koob/notifications';
import * as notificationsApi from 'api/node/notifications';
import AllotmentRoutes from './containers/Allotments/routes';
import NightRatesRoutes from './containers/NightRates/routes';
// KD-3735: pages for one organization; the group view asks which first.
import RequireActingOrganization from 'components/RequireActingOrganization';
import CacheProvider from 'providers/CacheProvider';
import LanguageProvider from 'providers/LanguageProvider';

const Users = lazy(() => import('containers/Users'));
const HotelsSettings = lazy(() => import('containers/HotelsSettings'));
const Hotels = lazy(() => import('containers/Hotels'));
const TravelersSettings = lazy(() => import('containers/TravelersSettings'));
const TravelDiarySettings = lazy(
  () => import('containers/TravelDiarySettings'),
);
const KoediaErrors = lazy(() => import('containers/Settings/KoediaErrors'));
const Locations = lazy(() => import('containers/Locations'));
const Organizations = lazy(() => import('containers/Organizations'));
const Billing = lazy(() => import('containers/Billing'));
const Bookings = lazy(() => import('containers/Bookings'));
const ToRequestAccess = lazy(() => import('containers/ToRequestAccess'));
const TripDesigner = lazy(() => import('containers/TripDesigner'));
const Trips = lazy(() => import('containers/Trips'));
const Settings = lazy(() => import('containers/Settings'));
const ProfileContainer = lazy(() => import('containers/Profile'));
const JobsContainer = lazy(() => import('containers/Jobs'));
const KoobUiShowcase = lazy(() => import('@koob/ui/src/pages/KoobUiShowcase'));
const KoobIconsGallery = lazy(
  () => import('@koob/ui/src/pages/KoobIconsGallery'),
);
const NotificationPreview = lazy(
  () => import('containers/NotificationPreview'),
);
const McpAuthorize = lazy(() => import('containers/McpAuthorize'));

const MapsWrapper = ({ children }) => {
  return (
    <KoobMapsProvider
      apiKey={GOOGLE_MAPS_API_KEY}
      mapId={GOOGLE_MAPS_MAP_ID}
      language="en"
    >
      {children}
    </KoobMapsProvider>
  );
};

const App = () => {
  const { accessToken, logout, isActiveToken } = useContext(AuthContext);
  const location = useLocation();
  usePageView(location);

  if (Boolean(!accessToken)) {
    // Carry the target so Login can come back (deep links, MCP consent).
    return (
      <Navigate
        to="/login"
        state={{ from: location.pathname + location.search }}
      />
    );
  }
  if (!isActiveToken) {
    logout();
  }

  return (
    <RoutesProvider>
      <AppProvider>
        <BookingProvider>
          <LanguageProvider>
            <TripDesignerConfigProvider>
              <NotificationsProvider api={notificationsApi}>
                <CacheProvider>
                  <Layout>
                    <MapsWrapper>
                      <Routes>
                        <Route
                          path="organizations/*"
                          element={<Organizations />}
                        />
                        <Route path="locations/*" element={<Locations />} />
                        <Route path="users/*" element={<Users />} />
                        <Route path="hotels/*" element={<Hotels />} />
                        <Route
                          path="experiences/*"
                          element={<ExperiencesRoutes />}
                        />
                        <Route path="billing/*" element={<Billing />} />
                        <Route path="bookings/*" element={<Bookings />} />
                        <Route
                          path="trip-designer/*"
                          element={<TripDesigner />}
                        />
                        <Route path="trips/*" element={<Trips />} />
                        <Route
                          path="to-request-access/*"
                          element={<ToRequestAccess />}
                        />
                        <Route
                          path="to-connection-request/*"
                          element={
                            <RequireActingOrganization>
                              <ToConnectionRequest />
                            </RequireActingOrganization>
                          }
                        />
                        <Route
                          path="night-rates/*"
                          element={
                            <RequireActingOrganization>
                              <NightRatesRoutes />
                            </RequireActingOrganization>
                          }
                        />
                        <Route
                          path="allotments/*"
                          element={
                            <RequireActingOrganization>
                              <AllotmentRoutes />
                            </RequireActingOrganization>
                          }
                        />
                        <Route
                          path="settings/hotels/*"
                          element={<HotelsSettings />}
                        />
                        <Route
                          path="settings/experiences/*"
                          element={<ExperienceSettingsRoutes />}
                        />
                        <Route
                          path="settings/travelers/*"
                          element={<TravelersSettings />}
                        />
                        <Route
                          path="settings/travel-diary/*"
                          element={<TravelDiarySettings />}
                        />
                        <Route
                          path="settings/error-dictionary/*"
                          element={<KoediaErrors />}
                        />
                        <Route path="settings/*" element={<Settings />} />
                        <Route
                          path="mcp/authorize"
                          element={<McpAuthorize />}
                        />
                        <Route path="user/*" element={<ProfileContainer />} />
                        <Route path="jobs/*" element={<JobsContainer />} />
                        <Route path="ui" element={<KoobUiShowcase />} />
                        <Route path="ui/icons" element={<KoobIconsGallery />} />
                        <Route
                          path="ui/notifications"
                          element={<NotificationPreview />}
                        />
                        <Route index element={<Navigate to="hotels" />} />
                      </Routes>
                    </MapsWrapper>
                  </Layout>
                </CacheProvider>
              </NotificationsProvider>
            </TripDesignerConfigProvider>
          </LanguageProvider>
        </BookingProvider>
      </AppProvider>
    </RoutesProvider>
  );
};

export default App;
